import * as THREE from 'three';

// Camera-facing cards for instanced far things (trees, people): a quad whose vertex shader
// turns it about its own foot toward the camera, keeping the instance's position and scale and
// ignoring its rotation. With `cards` > 1 the texture is an atlas of that many columns and each
// instance picks one through a per-instance `card` attribute (cardQuad gives the geometry).
// Legacy renderer only: the hook is onBeforeCompile.

const TOCAM = `
  #ifdef USE_INSTANCING
    toCam = cameraPosition - ( modelMatrix * vec4( instanceMatrix[ 3 ].xyz, 1.0 ) ).xyz;
    toCam.y = 0.0;
    toCam = normalize( toCam );
  #endif
  #include <beginnormal_vertex>
`;
const NORMAL = `#include <defaultnormal_vertex>
  #ifdef USE_INSTANCING
    transformedNormal = normalize( ( viewMatrix * vec4( normalize( toCam + vec3( 0.0, 0.6, 0.0 ) ), 0.0 ) ).xyz );
  #endif
`;
const PROJECT = `
  vec4 mvPosition = vec4( transformed, 1.0 );
  #ifdef USE_INSTANCING
    vec3 ip = ( modelMatrix * vec4( instanceMatrix[ 3 ].xyz, 1.0 ) ).xyz;
    float s = length( instanceMatrix[ 0 ].xyz );
    vec3 right = normalize( cross( vec3( 0.0, 1.0, 0.0 ), toCam ) );
    vec3 world = ip + right * ( transformed.x * s ) + vec3( 0.0, transformed.y * s, 0.0 );
    mvPosition = viewMatrix * vec4( world, 1.0 );
  #else
    mvPosition = modelViewMatrix * mvPosition;
  #endif
  gl_Position = projectionMatrix * mvPosition;
`;

export function billboardMaterial(map, { cards = 1, rows = 1, alphaTest = 0.5 } = {}) {
  const material = new THREE.MeshLambertMaterial({ map, alphaTest, side: THREE.DoubleSide });
  material.defines = {};
  if (cards > 1) material.defines.CARDS = cards.toFixed(1);
  if (rows > 1) material.defines.ROWS = rows.toFixed(1);
  material.onBeforeCompile = (shader) => {
    let v = shader.vertexShader
      .replace('void main() {', (cards > 1 ? 'attribute float card;\n' : '') + (rows > 1 ? 'attribute float frame;\n' : '') + 'vec3 toCam;\nvoid main() {')
      .replace('#include <beginnormal_vertex>', TOCAM)
      .replace('#include <defaultnormal_vertex>', NORMAL)
      .replace('#include <project_vertex>', PROJECT);
    if (cards > 1) v = v.replace('#include <uv_vertex>', '#include <uv_vertex>\n#ifdef USE_INSTANCING\n  vMapUv.x = ( vMapUv.x + card ) / CARDS;\n#endif');
    if (rows > 1) v = v.replace('#include <uv_vertex>', '#include <uv_vertex>\n#ifdef USE_INSTANCING\n  vMapUv.y = ( vMapUv.y + frame ) / ROWS;\n#endif');
    shader.vertexShader = v;
  };
  return material;
}

// A card standing on its foot; with `slots`, per-instance `card` (atlas column) and `frame` (atlas
// row) attributes.
export function cardQuad(width, height, slots = 0) {
  const g = new THREE.PlaneGeometry(width, height).translate(0, height / 2, 0);
  if (slots) {
    g.setAttribute('card', new THREE.InstancedBufferAttribute(new Float32Array(slots), 1));
    g.setAttribute('frame', new THREE.InstancedBufferAttribute(new Float32Array(slots), 1));
  }
  return g;
}
